import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { RecordItem } from '../models/RecordItem';
import { recordsService, CreateRecordData, UpdateRecordData, RecordUsersResponse, GrantPermissionData } from '../services/recordsService';
import { useAppContext } from '../contexts/Storage';

export const useRecords = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<RecordItem[]>([]);
  const { token, user } = useAppContext();

  // Separate loading states for different operations
  const [isCreating, setIsCreating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isManagingPermissions, setIsManagingPermissions] = useState(false);

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!user?.id) {
        throw new Error('User ID not found');
      }

      // Pass user ID in the filter as required by the API
      const result = await recordsService.getRecords(token, {
        user_id: user.id
      });

      if (result.success && result.data) {
        setRecords(result.data);
        return { success: true, data: result.data };
      } else {
        setError(result.error || 'Failed to fetch records');
        toast.error(result.error || 'Failed to fetch records');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [token, user]);

  const createRecord = useCallback(async (data: CreateRecordData) => {
    setIsCreating(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!user?.id) {
        throw new Error('User ID not found');
      }

      const result = await recordsService.createRecord(token, {
        ...data,
        user_id: user.id,
        record_creator_id: user.id,
        created_at: new Date().toISOString().replace('T', ' ').substring(0, 19)
      });

      if (result.success && result.data) {
        // Refresh the records list to ensure we have the latest data
        await fetchRecords();
        toast.success('Record created successfully');
        return { success: true, data: result.data };
      } else {
        setError(result.error || 'Failed to create record');
        toast.error(result.error || 'Failed to create record');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsCreating(false);
    }
  }, [token, user, fetchRecords]);

  const updateRecord = useCallback(async (id: number, data: UpdateRecordData) => {
    setIsUpdating(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!user?.id) {
        throw new Error('User ID not found');
      }

      const result = await recordsService.updateRecord(token, id, {
        ...data,
        user_id: user.id
      });

      if (result.success && result.data) {
        // Refresh the records list to ensure we have the latest data
        await fetchRecords();
        toast.success('Record updated successfully');
        return { success: true, data: result.data };
      } else {
        setError(result.error || 'Failed to update record');
        toast.error(result.error || 'Failed to update record');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsUpdating(false);
    }
  }, [token, user, fetchRecords]);

  const deleteRecord = useCallback(
    async (id: number, creatorId: number, verificationCode: string) => {
      setIsDeleting(true);
      setError(null);

      try {
        if (!token) {
          throw new Error('No authentication token found');
        }

        const result = await recordsService.deleteRecord(
          token,
          id,
          creatorId,
          verificationCode
        );

        if (result.success) {
          await fetchRecords();
          toast.success('Record deleted successfully');
          return { success: true };
        } else {
          setError(result.error || 'Failed to delete record');
          toast.error(result.error || 'Failed to delete record');
          return { success: false, error: result.error };
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsDeleting(false);
      }
    },
    [token, fetchRecords]
  );

  const joinRecordByCode = useCallback(async (code: string) => {
    setIsJoining(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!user?.id) {
        throw new Error('User ID not found');
      }

      const result = await recordsService.joinRecordByCode(token, code, user.id);

      if (result.success && result.data) {
        // Refresh records after joining
        await fetchRecords();
        toast.success('Successfully joined the record');
        return { success: true, data: result.data };
      } else {
        setError(result.error || 'Failed to join record');
        toast.error(result.error || 'Failed to join record');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsJoining(false);
    }
  }, [token, user, fetchRecords]);

  const exportRecord = useCallback(async (id: number) => {
    setIsExporting(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await recordsService.exportRecord(token, id);

      if (result.success && result.data) {
        // Convert JSON data to string and create a Blob
        const jsonString = JSON.stringify(result.data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        
        // Create a download link for the blob
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `record-${id}-export.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Record exported successfully');
        return { success: true, data: result.data };
      } else {
        setError(result.error || 'Failed to export record');
        toast.error(result.error || 'Failed to export record');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsExporting(false);
    }
  }, [token]);

  const importRecord = useCallback(async (jsonData: string) => {
    setIsImporting(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await recordsService.importRecord(token, jsonData);

      if (result.success && result.data) {
        // Refresh records after import
        await fetchRecords();
        toast.success('Record imported successfully');
        return { success: true, data: result.data };
      } else {
        setError(result.error || 'Failed to import record');
        toast.error(result.error || 'Failed to import record');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsImporting(false);
    }
  }, [token, fetchRecords]);

  // Permissions management methods
  const getRecordUsers = useCallback(async (recordId: number) => {
    setIsManagingPermissions(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await recordsService.getRecordUsers(token, recordId);

      if (result.success && result.data) {
        return { success: true, data: result.data };
      } else {
        setError(result.error || 'Failed to fetch record users');
        toast.error(result.error || 'Failed to fetch record users');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsManagingPermissions(false);
    }
  }, [token]);

  const grantPermission = useCallback(async (userId: number, data: GrantPermissionData) => {
    setIsManagingPermissions(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await recordsService.grantPermission(token, userId, data);

      if (result.success) {
        toast.success('Permissions updated successfully');
        return { success: true };
      } else {
        setError(result.error || 'Failed to update permissions');
        toast.error(result.error || 'Failed to update permissions');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsManagingPermissions(false);
    }
  }, [token]);

  const removeUserFromRecord = useCallback(async (userId: number, recordId: number, targetUserId: number) => {
    setIsManagingPermissions(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await recordsService.removeUserFromRecord(token, userId, recordId, targetUserId);

      if (result.success) {
        toast.success('User removed from record successfully');
        return { success: true };
      } else {
        setError(result.error || 'Failed to remove user from record');
        toast.error(result.error || 'Failed to remove user from record');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsManagingPermissions(false);
    }
  }, [token]);

  return {
    records,
    isLoading,
    error,
    fetchRecords,
    createRecord,
    updateRecord,
    deleteRecord,
    joinRecordByCode,
    exportRecord,
    importRecord,
    // Permissions management
    getRecordUsers,
    grantPermission,
    removeUserFromRecord,
    setRecords,
    // Separate loading states
    isCreating,
    isExporting,
    isImporting,
    isJoining,
    isUpdating,
    isDeleting,
    isManagingPermissions
  };
};